import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, CardContent, Button,
  Toolbar, IconButton, Chip, alpha, Popover, Tooltip,
  List, ListItemButton, ListItemText, Divider,
} from '@mui/material';
import {
  Event as EventIcon,
  EventAvailable as EventSidebarIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarMonthIcon, Add as AddIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CloudSync as CloudSyncIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Calendar, Navigate, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './EventCalendar.css';
import { useCalendarEvents } from '../../../hooks/useCalendarEvents';
import { useAuth } from '../../../contexts/AuthContext';
import ListFilterBar from '../../common/ListFilterBar';
import StatCards from '../../common/StatCards';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { ACCENT, glassCardSx } from '../../common/SaaSTokens';
import { googleCalendarService } from '../../../services/googleCalendarService';

const localizer = momentLocalizer(moment);

const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const TEXT_PRIMARY   = 'rgba(226,232,240,0.88)';
const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

const CAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes bar-sweep  { 0%{background-position:-200% 0} 100%{background-position:200% 0} }

  .rbc-calendar {
    background: transparent !important;
    color: ${TEXT_PRIMARY} !important;
    font-family: 'DM Sans', sans-serif !important;
    min-height: 0 !important;
  }

  .rbc-header {
    background: rgba(17,24,39,0.85) !important;
    border-color: rgba(255,255,255,0.08) !important;
    color: ${alpha(ACCENT, 0.85)} !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.63rem !important;
    letter-spacing: 1.4px !important;
    text-transform: uppercase !important;
    padding: 9px 4px !important;
    font-weight: 600 !important;
  }

  .rbc-today { background: rgba(59,130,246,0.06) !important; }
  .rbc-off-range-bg { background: rgba(2,6,23,0.55) !important; }

  .rbc-month-view,
  .rbc-time-view {
    border-color: rgba(255,255,255,0.08) !important;
    border-radius: 10px !important;
    overflow: hidden;
    background: transparent !important;
  }

  .rbc-day-bg + .rbc-day-bg,
  .rbc-month-row + .rbc-month-row,
  .rbc-header + .rbc-header,
  .rbc-time-slot,
  .rbc-timeslot-group,
  .rbc-time-content > * + * > * {
    border-color: rgba(255,255,255,0.06) !important;
  }

  .rbc-date-cell {
    color: rgba(203,213,225,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.72rem !important;
    padding: 4px 8px !important;
    font-weight: 500 !important;
  }
  .rbc-date-cell.rbc-now {
    color: ${alpha(ACCENT, 0.95)} !important;
    font-weight: 700 !important;
  }
  .rbc-date-cell button {
    color: inherit !important;
  }

  .rbc-show-more {
    color: ${alpha(ACCENT, 0.9)} !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.62rem !important;
    font-weight: 600 !important;
    background: rgba(59,130,246,0.08) !important;
    border-radius: 4px !important;
    padding: 1px 5px !important;
  }

  .rbc-label {
    color: ${alpha(ACCENT, 0.5)} !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.62rem !important;
    font-weight: 500 !important;
  }
  .rbc-time-header-content { border-color: rgba(255,255,255,0.08) !important; }
  .rbc-current-time-indicator {
    background: ${alpha(ACCENT, 0.75)} !important;
    box-shadow: 0 0 8px ${alpha(ACCENT, 0.45)} !important;
  }
`;

const MotionCard = motion(Box);

const EventCalendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const { calendarEvents, loading, error, setError } = useCalendarEvents();

  const [view,            setView]            = useState(Views.MONTH);
  const [date,            setDate]            = useState(new Date());
  const [searchTerm,      setSearchTerm]      = useState('');
  const [showFilters,     setShowFilters]     = useState(false);
  const [filters,         setFilters]         = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [jumpPickerAnchor, setJumpPickerAnchor] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [googleActionsAnchor, setGoogleActionsAnchor] = useState(null);

  useEffect(() => {
    const cssId = 'eventcal-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId;
      el.innerHTML = CAL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (view === Views.AGENDA) setView(Views.MONTH);
  }, [view]);

  useEffect(() => {
    if (!currentUser) return;

    const loadStatus = async () => {
      try {
        const status = await googleCalendarService.getStatus();
        setGoogleConnected(Boolean(status.connected));
        setGoogleEmail(status.google_email || '');
      } catch {
        setGoogleConnected(false);
      }
    };

    loadStatus();
  }, [currentUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const syncState = params.get('google_sync');
    if (!syncState) return;

    if (syncState === 'connected') {
      setSyncInfo('Google Calendar connected successfully.');
      setGoogleConnected(true);
    } else {
      setSyncInfo('Google Calendar connection failed. Please try again.');
    }
  }, [location.search]);

  /** After OAuth, backfill events that were never synced (create/update only syncs at save time). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('google_sync') !== 'connected' || !googleConnected) return;

    const key = 'robocon_gcal_backfill_v1';
    if (sessionStorage.getItem(key)) return;

    let cancelled = false;
    (async () => {
      try {
        const r = await googleCalendarService.syncExisting();
        if (cancelled) return;
        sessionStorage.setItem(key, '1');
        const extra =
          typeof r?.synced === 'number'
            ? ` Synced ${r.synced} event(s) to Google${r.failed ? ` (${r.failed} could not sync — check logs).` : '.'}`
            : '';
        setSyncInfo((prev) => (prev ? `${prev}${extra}` : `Google Calendar updated.${extra}`));
      } catch {
        if (!cancelled) {
          setSyncInfo((prev) =>
            prev
              ? `${prev} Could not auto-sync existing events — use “Push events to Google” below.`
              : 'Use “Push events to Google” to copy existing events into your calendar.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, googleConnected]);

  const handleConnectGoogleCalendar = async () => {
    try {
      setSyncLoading(true);
      const url = await googleCalendarService.getConnectUrl();
      if (!url) {
        setSyncInfo('Unable to start Google Calendar connection.');
        return;
      }
      window.location.href = url;
    } catch {
      setSyncInfo('Unable to start Google Calendar connection.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handlePushExistingToGoogle = async () => {
    try {
      setSyncLoading(true);
      const r = await googleCalendarService.syncExisting();
      setSyncInfo(
        typeof r?.synced === 'number'
          ? `Pushed ${r.synced} event(s) to Google Calendar${r.failed ? ` (${r.failed} failed).` : '.'}`
          : 'Sync finished.',
      );
    } catch {
      setSyncInfo('Could not push events to Google. Try again or check the server log.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    try {
      setSyncLoading(true);
      await googleCalendarService.disconnect();
      setGoogleConnected(false);
      setGoogleEmail('');
      sessionStorage.removeItem('robocon_gcal_backfill_v1');
      setSyncInfo('Google Calendar disconnected.');
    } catch {
      setSyncInfo('Failed to disconnect Google Calendar.');
    } finally {
      setSyncLoading(false);
    }
  };

  const filterOptions = [
    {
      name: 'type', label: 'Event Type', type: 'select',
      options: [
        { value: 'Demo', label: 'Demo' }, { value: 'Prototype', label: 'Prototype' },
        { value: 'Training', label: 'Training' }, { value: 'LabCleaning', label: 'Lab Cleaning' },
        { value: 'Other', label: 'Other' },
      ],
    },
    {
      name: 'status', label: 'Status', type: 'select',
      options: [
        { value: 'Pending', label: 'Pending' }, { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' }, { value: 'Cancelled', label: 'Cancelled' },
      ],
    },
    {
      name: 'date_range', label: 'Date Range', type: 'select',
      options: [
        { value: 'today', label: 'Today' }, { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' }, { value: 'upcoming', label: 'Upcoming' },
        { value: 'past', label: 'Past Events' },
      ],
    },
  ];

  const isDateInRange = (eventDate, range) => {
    const today = dayjs();
    const eventDay = dayjs(eventDate);
    switch (range) {
      case 'today':      return eventDay.isSame(today, 'day');
      case 'this_week':  return eventDay.isSame(today, 'week');
      case 'this_month': return eventDay.isSame(today, 'month');
      case 'upcoming':   return eventDay.isAfter(today);
      case 'past':       return eventDay.isBefore(today);
      default:           return true;
    }
  };

  const filteredEvents = useMemo(() => calendarEvents.filter(event => {
    const matchesSearch =
      event.resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.resource.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.resource.remark && event.resource.remark.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      switch (key) {
        case 'type':       return event.resource.type === value;
        case 'status':     return event.resource.status === value;
        case 'date_range': return isDateInRange(event.resource.start_date, value);
        default:           return true;
      }
    });
    return matchesSearch && matchesFilters;
  }), [calendarEvents, searchTerm, filters]);

  const agendaRange = useMemo(() => {
    const d = dayjs(date);
    if (view === Views.WEEK) {
      return { start: d.startOf('week'), end: d.endOf('week') };
    }
    if (view === Views.DAY) {
      return { start: d.startOf('day'), end: d.endOf('day') };
    }
    return { start: d.startOf('month'), end: d.endOf('month') };
  }, [date, view]);

  const agendaTitle = useMemo(() => {
    const { start, end } = agendaRange;
    if (view === Views.DAY) return start.format('dddd, MMMM D, YYYY');
    if (view === Views.WEEK) {
      return start.month() === end.month()
        ? `${start.format('MMM D')} – ${end.format('D, YYYY')}`
        : `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
    }
    return start.format('MMMM YYYY');
  }, [agendaRange, view]);

  const agendaItems = useMemo(() => {
    const rs = agendaRange.start;
    const re = agendaRange.end;
    return filteredEvents
      .filter((ev) => {
        const eventStart = dayjs(ev.start);
        const eventEnd = dayjs(ev.end);
        return eventStart.isBefore(re) && eventEnd.isAfter(rs);
      })
      .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());
  }, [filteredEvents, agendaRange]);

  const handleSelectEvent = useCallback((calEvent) => {
    const id = calEvent.resource?.id;
    if (id != null) navigate(`/events/view/${id}`);
  }, [navigate]);

  const handleSelectSlot = useCallback(({ start, end }) => {
    const startD = dayjs(start);
    const endD = dayjs(end);

    // In month view there is no time selection; keep time pickers blank.
    const startTime = startD.hour() === 0 && startD.minute() === 0 ? null : startD.format('HH:mm');
    const endTime = endD.hour() === 0 && endD.minute() === 0 ? null : endD.format('HH:mm');

    let url = `/events/create?start_date=${startD.format('YYYY-MM-DD')}&end_date=${endD.format('YYYY-MM-DD')}`;
    if (startTime) url += `&start_time=${startTime}`;
    if (endTime) url += `&end_time=${endTime}`;
    navigate(url);
  }, [navigate]);

  const handleNavigate = useCallback((newDate) => setDate(newDate), []);
  const handleViewChange = useCallback((newView) => setView(newView), []);

  const statsData = [
    { icon: EventIcon,          value: calendarEvents.length,                                                                  label: 'Total Events', color: 'info'      },
    { icon: ScheduleIcon,       value: calendarEvents.filter(e => dayjs(e.start).isAfter(dayjs())).length,                        label: 'Upcoming',     color: 'success'   },
    { icon: HourglassEmptyIcon, value: calendarEvents.filter(e => e.resource?.status === 'In Progress').length,                   label: 'In Progress',  color: 'warning'   },
    { icon: CheckCircleIcon,    value: calendarEvents.filter(e => e.resource?.status === 'Completed').length,                     label: 'Completed',    color: 'secondary' },
  ];

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'upcoming', 'in-progress', 'completed'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    delete nextFilters.date_range;
    delete nextFilters.status;

    if (nextFilter === 'upcoming') nextFilters.date_range = 'upcoming';
    if (nextFilter === 'in-progress') nextFilters.status = 'In Progress';
    if (nextFilter === 'completed') nextFilters.status = 'Completed';

    setFilters(nextFilters);
  };

  const activeStatIndex =
    activeStatFilter === 'upcoming' ? 1 :
    activeStatFilter === 'in-progress' ? 2 :
    activeStatFilter === 'completed' ? 3 :
    0;

  const EventComponent = ({ event }) => {
    const accent = event.resource.familyAccent || event.resource.color;
    const isPhase = event.resource.isPhase;
    return (
      <Box sx={{
        backgroundColor: event.resource.color,
        color: event.resource.textColor || '#fff',
        py: '2px', px: '5px', borderRadius: '4px',
        fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        borderLeft: isPhase ? `3px solid ${accent}` : 'none',
        pl: isPhase ? '4px' : '5px',
        opacity: 1,
        '&:hover': { filter: 'brightness(1.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' },
      }}
      >
        {event.title}
      </Box>
    );
  };

  const navBtnSx = {
    borderRadius: '8px', color: ACCENT, minWidth: 34, width: 34, height: 34,
    border: '1px solid rgba(255,255,255,0.12)',
    background: alpha(SURFACE_UP, 0.6),
    transition: 'all 0.2s ease',
    '&:hover': { background: alpha(ACCENT, 0.12), borderColor: alpha(ACCENT, 0.35) },
  };
  const viewBtnSx = (active) => ({
    borderRadius: '8px', px: 1.5, py: 0.5, minWidth: 0,
    fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', fontWeight: 600, letterSpacing: 0.8,
    textTransform: 'uppercase',
    ...(active
      ? { background: `linear-gradient(135deg, ${alpha(ACCENT, 0.22)}, ${alpha(B[700], 0.55)})`, border: `1px solid ${alpha(ACCENT, 0.4)}`, color: ACCENT }
      : { background: alpha(SURFACE_UP, 0.5), border: '1px solid rgba(255,255,255,0.1)', color: alpha(ACCENT, 0.55), '&:hover': { borderColor: alpha(ACCENT, 0.3), color: ACCENT, background: alpha(ACCENT, 0.06) } }
    ),
    transition: 'all 0.2s ease',
  });

  const jumpPickerCalendarSx = {
    maxWidth: '100%',
    bgcolor: 'transparent',
    '& .MuiPickersCalendarHeader-root': {
      pl: 1, pr: 1, my: 0.5,
      '& .MuiPickersCalendarHeader-label': { color: alpha('#E2E8F0', 0.92), fontFamily: "'DM Sans', sans-serif", fontWeight: 600 },
    },
    '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': { color: alpha(ACCENT, 0.85) },
    '& .MuiDayCalendar-weekDayLabel': { color: alpha('#94A3B8', 0.9), fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem' },
    '& .MuiPickersDay-root': {
      color: alpha('#E2E8F0', 0.9),
      fontFamily: "'DM Sans', sans-serif",
      '&:hover': { bgcolor: alpha(ACCENT, 0.12) },
      '&.Mui-selected': { bgcolor: `${alpha(ACCENT, 0.35)} !important`, color: '#fff' },
    },
    '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
      color: alpha('#E2E8F0', 0.88),
      '&:hover': { bgcolor: alpha(ACCENT, 0.1) },
      '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.4), color: '#fff' },
    },
  };

  const CustomToolbar = ({ label, date: toolbarDate, onNavigate, onView, view: currentView }) => (
    <Toolbar sx={{
      justifyContent: 'space-between', mb: 2, minHeight: '52px !important',
      background: `linear-gradient(135deg, ${alpha(ACCENT, 0.06)}, ${alpha(SURFACE_MID, 0.6)})`,
      borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(8px)', px: '12px !important', gap: 1,
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => onNavigate('PREV')} sx={navBtnSx} aria-label="Previous"><ChevronLeftIcon sx={{ fontSize: 18 }} /></IconButton>
        <Tooltip title="Go to date — year, month, and day" arrow>
          <IconButton
            size="small"
            onClick={(e) => setJumpPickerAnchor(e.currentTarget)}
            sx={navBtnSx}
            aria-label="Go to date"
            aria-haspopup="dialog"
          >
            <CalendarMonthIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => onNavigate('NEXT')} sx={navBtnSx} aria-label="Next"><ChevronRightIcon sx={{ fontSize: 18 }} /></IconButton>
        <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1rem', letterSpacing: 0.5, color: alpha('#E2E8F0', 0.88), ml: 1 }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {[Views.MONTH, Views.WEEK, Views.DAY].map((v) => (
          <Button key={v} onClick={() => onView(v)} sx={viewBtnSx(currentView === v)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
      </Box>

      <Popover
        open={Boolean(jumpPickerAnchor)}
        anchorEl={jumpPickerAnchor}
        onClose={() => setJumpPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: '12px',
              background: `linear-gradient(180deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#0B1220', 0.98)} 100%)`,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ p: 1, pt: 1.5 }}>
            <DateCalendar
              value={dayjs(toolbarDate)}
              onChange={(v) => {
                if (v) {
                  onNavigate(Navigate.DATE, v.startOf('day').toDate());
                  setJumpPickerAnchor(null);
                }
              }}
              views={['year', 'month', 'day']}
              sx={jumpPickerCalendarSx}
            />
            <Box sx={{ px: 1, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => { onNavigate(Navigate.TODAY); setJumpPickerAnchor(null); }}
                sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, textTransform: 'none', color: ACCENT }}
              >
                Today
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </Popover>
    </Toolbar>
  );

  if (!currentUser) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading calendar" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Event Calendar"
        subtitle="View and manage all events and activities"
        icon={EventSidebarIcon}
        actionButton={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip
              size="small"
              icon={googleConnected ? <LinkIcon /> : <LinkOffIcon />}
              label={googleConnected ? (googleEmail ? `Google: ${googleEmail}` : 'Google Connected') : 'Google Not Connected'}
              sx={{
                height: 30,
                maxWidth: { xs: 210, sm: 260 },
                fontFamily: "'DM Sans', sans-serif",
                color: googleConnected ? alpha('#86efac', 0.95) : alpha('#fca5a5', 0.95),
                bgcolor: googleConnected ? alpha('#16a34a', 0.12) : alpha('#dc2626', 0.12),
                border: `1px solid ${googleConnected ? alpha('#22c55e', 0.28) : alpha('#ef4444', 0.28)}`,
                '& .MuiChip-icon': { color: 'inherit' },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: 150, sm: 200 },
                },
              }}
            />
            <Button
              variant="outlined"
              size="medium"
              startIcon={googleConnected ? <CloudSyncIcon /> : <LinkIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setGoogleActionsAnchor(e.currentTarget)}
              disabled={syncLoading}
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: '10px',
                px: 1.75,
                py: 0.95,
                border: `1px solid ${alpha(ACCENT, 0.36)}`,
                color: alpha('#bfdbfe', 0.95),
                bgcolor: alpha('#1e3a8a', 0.22),
                '&:hover': { borderColor: alpha(ACCENT, 0.58), bgcolor: alpha('#1e3a8a', 0.36) },
              }}
            >
              Google Sync
            </Button>
            <Button
              variant="contained" size="medium" startIcon={<AddIcon />}
              onClick={() => navigate('/events/create')}
              sx={{
                fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
                fontSize: '0.86rem', borderRadius: '10px', px: 2.25, py: 1.05,
                bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                transition: 'all 0.22s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'scale(1.02)' },
              }}
            >
              Create Event
            </Button>
            <Popover
              open={Boolean(googleActionsAnchor)}
              anchorEl={googleActionsAnchor}
              onClose={() => setGoogleActionsAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.75,
                    minWidth: 220,
                    borderRadius: '10px',
                    background: `linear-gradient(180deg, ${alpha('#0f172a', 0.98)} 0%, ${alpha('#0b1220', 0.98)} 100%)`,
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: '0 16px 36px rgba(0,0,0,0.45)',
                  },
                },
              }}
            >
              <List dense disablePadding sx={{ py: 0.5 }}>
                {googleConnected ? (
                  <>
                    <ListItemButton
                      disabled={syncLoading}
                      sx={{
                        color: alpha('#E2E8F0', 0.95),
                        '&:hover': { bgcolor: alpha(ACCENT, 0.1) },
                      }}
                      onClick={() => {
                        setGoogleActionsAnchor(null);
                        handlePushExistingToGoogle();
                      }}
                    >
                      <CloudSyncIcon sx={{ mr: 1.2, fontSize: 18, color: alpha('#93c5fd', 0.95) }} />
                      <ListItemText
                        primary="Push events to Google"
                        primaryTypographyProps={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.85rem',
                          color: alpha('#E2E8F0', 0.95),
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                    <ListItemButton
                      disabled={syncLoading}
                      sx={{
                        color: alpha('#E2E8F0', 0.95),
                        '&:hover': { bgcolor: alpha('#ef4444', 0.1) },
                      }}
                      onClick={() => {
                        setGoogleActionsAnchor(null);
                        handleDisconnectGoogleCalendar();
                      }}
                    >
                      <LinkOffIcon sx={{ mr: 1.2, fontSize: 18, color: alpha('#fca5a5', 0.95) }} />
                      <ListItemText
                        primary="Disconnect Google"
                        primaryTypographyProps={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.85rem',
                          color: alpha('#F8FAFC', 0.95),
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </>
                ) : (
                  <ListItemButton
                    disabled={syncLoading}
                    sx={{
                      color: alpha('#E2E8F0', 0.95),
                      '&:hover': { bgcolor: alpha(ACCENT, 0.1) },
                    }}
                    onClick={() => {
                      setGoogleActionsAnchor(null);
                      handleConnectGoogleCalendar();
                    }}
                  >
                    <LinkIcon sx={{ mr: 1.2, fontSize: 18, color: alpha('#93c5fd', 0.95) }} />
                    <ListItemText
                      primary="Connect Google"
                      primaryTypographyProps={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '0.85rem',
                        color: alpha('#E2E8F0', 0.95),
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                )}
              </List>
            </Popover>
          </Box>
        )}
      />

      <FeedbackMessage
        message={syncInfo}
        severity="info"
        onClose={() => setSyncInfo(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#0f172a', 0.9),
          border: `1px solid ${alpha(ACCENT, 0.25)}`,
          color: alpha('#bfdbfe', 0.95),
          mb: 3,
        }}
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
          '& .MuiAlert-icon': { color: '#f87171' },
        }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />
      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={filters}
        onFilterChange={(newFilters) => {
          const nextActive =
            newFilters.status === 'In Progress' ? 'in-progress' :
            newFilters.status === 'Completed' ? 'completed' :
            newFilters.date_range === 'upcoming' ? 'upcoming' :
            'all';
          setActiveStatFilter(nextActive);
          setFilters(newFilters);
        }}
        onClearFilters={() => {
          setFilters({});
          setActiveStatFilter('all');
          setSearchTerm('');
        }}
        filterOptions={filterOptions}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        searchPlaceholder="Search by event name, venue, or remark..."
      />

      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        sx={{
          ...glassCardSx,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: SCAN_LINES, pointerEvents: 'none', zIndex: 0, opacity: 0.5 }} />

        <CardContent
          sx={{
            p: 2.5, position: 'relative', zIndex: 1,
            height: { xs: 640, sm: 720 },
            display: 'flex', flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
            <Calendar
              localizer={localizer}
              events={filteredEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              views={[Views.MONTH, Views.WEEK, Views.DAY]}
              date={date}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              style={{ height: '100%' }}
              components={{ event: EventComponent, toolbar: CustomToolbar }}
              eventPropGetter={(event) => ({
                style: {
                  backgroundColor: event.resource.color,
                  borderColor: event.resource.color,
                  color: event.resource.textColor || '#fff',
                  border: 'none', borderRadius: '4px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  cursor: 'pointer',
                  marginLeft: 0,
                  opacity: 1,
                  borderLeft: event.resource.isPhase
                    ? `3px solid ${event.resource.familyAccent || event.resource.color}`
                    : undefined,
                },
              })}
              dayPropGetter={(d) => ({
                style: {
                  backgroundColor: dayjs(d).isSame(dayjs(), 'day')
                    ? alpha(ACCENT, 0.05)
                    : 'transparent',
                },
              })}
            />
          </Box>
        </CardContent>
      </MotionCard>

      <MotionCard
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.25 }}
        sx={{
          ...glassCardSx,
          position: 'relative',
          overflow: 'hidden',
          mt: 3,
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0, opacity: 0.6 }} />
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1.05rem', color: alpha('#E2E8F0', 0.9) }}>
              Schedule — {agendaTitle}
            </Typography>
            <Chip
              size="small"
              label={view === Views.MONTH ? 'Month' : view === Views.WEEK ? 'Week' : 'Day'}
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.62rem',
                fontWeight: 600,
                color: alpha(ACCENT, 0.9),
                borderColor: alpha(ACCENT, 0.35),
                bgcolor: alpha(ACCENT, 0.08),
              }}
              variant="outlined"
            />
          </Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: alpha('#94A3B8', 0.95), mb: 1.5 }}>
            Events and training phases in the same range as the calendar above. Demo / Prototype phases share a colored edge with the parent event type.
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 0 }} />
          <List
            dense
            disablePadding
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              mt: 0,
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': { background: alpha(ACCENT, 0.25), borderRadius: 4 },
            }}
          >
            {agendaItems.length === 0 ? (
              <Typography sx={{ py: 3, textAlign: 'center', color: alpha(ACCENT, 0.45), fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem' }}>
                No events in this range.
              </Typography>
            ) : (
              agendaItems.map((ev, idx) => {
                const r = ev.resource;
                const sameDay = dayjs(ev.start).isSame(dayjs(ev.end), 'day');
                const timeFmt = 'h:mm A';
                const rangeLabel = ev.allDay
                  ? dayjs(ev.start).format('MMM D, YYYY')
                  : sameDay
                    ? `${dayjs(ev.start).format('MMM D')} · ${dayjs(ev.start).format(timeFmt)} – ${dayjs(ev.end).format(timeFmt)}`
                    : `${dayjs(ev.start).format('MMM D, h:mm A')} – ${dayjs(ev.end).format('MMM D, h:mm A')}`;
                return (
                  <React.Fragment key={ev.id}>
                    {idx > 0 ? <Divider component="li" sx={{ borderColor: 'rgba(255,255,255,0.06)' }} /> : null}
                    <ListItemButton
                      onClick={() => handleSelectEvent(ev)}
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        alignItems: 'flex-start',
                        borderLeft: r.isPhase ? `3px solid ${r.familyAccent || r.color}` : '3px solid transparent',
                        '&:hover': { bgcolor: alpha(ACCENT, 0.06) },
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: r.color,
                          flexShrink: 0,
                          mt: 0.6,
                          mr: 1.25,
                          boxShadow: `0 0 0 2px ${alpha(r.color, 0.25)}`,
                        }}
                      />
                      <ListItemText
                        primary={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography component="span" sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: alpha('#E2E8F0', 0.95) }}>
                              {r.name}
                            </Typography>
                            {r.isPhase && r.phase ? (
                              <Chip
                                label={r.phase}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  fontFamily: "'JetBrains Mono', monospace",
                                  color: alpha('#fff', 0.95),
                                  bgcolor: alpha(r.color, 0.85),
                                }}
                              />
                            ) : null}
                          </Box>
                        )}
                        secondary={(
                          <Box component="span" sx={{ display: 'block', mt: 0.35 }}>
                            <Typography component="span" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', color: alpha(ACCENT, 0.75), display: 'block' }}>
                              {rangeLabel}
                            </Typography>
                            <Typography component="span" sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: alpha('#94A3B8', 0.9), display: 'block' }}>
                              {[r.type, r.status].filter(Boolean).join(' · ')}
                              {r.venue ? ` · ${r.venue}` : ''}
                            </Typography>
                          </Box>
                        )}
                        slotProps={{ secondary: { component: 'div' } }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })
            )}
          </List>
        </CardContent>
      </MotionCard>
    </Container>
  );
};

export default EventCalendar;
